import React, { useEffect, useState } from "react";
import { CartItem, ProductItemInterface, VariantItem } from "../../../assets/interfaces";
import {
  capitalizeFirstLetter,
  enumToHumanFriendly,
  objectIsFilled,
  toCurrency,
} from "../../../assets/js/utils/functions";
import {
  checkItemAvailability,
  getActualPrice,
  getCustomVariantOption,
  getItemThumbnail,
} from "../../../assets/js/utils/utils";
import RoundCheckbox from "../../ui/form-elements/round-checkbox";
import { FormatCurrencyFun } from "../../../contexts/cart-context";
import classNames from "classnames";
import { SelectDropdown } from "@/components/ui/form-elements";
import useSteps from "@/components/hooks/useSteps";
import QuantityUpdate from "./quantity-update";
import { AppBtn } from "@/components/ui/buttons";
import { StateType, VariantsInternalStateItem } from "../modals/select-variant";

interface Props {
  variantOptions: string[];
  formatAsCurrency: FormatCurrencyFun;
  product: ProductItemInterface;
  optionKeys: string[];
  internalSelected: VariantsInternalStateItem[];
  updateOptionState: (index: number, state: StateType) => void;
  updateOptionQuantity: (index: number, quantity: number) => void;
  deleteOption: (index: number) => void;
  updateOptionValues: (index: number, option: string, value: string) => void;
  saveSelections: (index: number) => void;
  addNewOption: () => void;
}

const SelectMultipleCustomOptions: React.FC<Props> = ({
  formatAsCurrency,
  product,
  variantOptions,
  optionKeys,
  internalSelected,
  updateOptionState,
  updateOptionQuantity,
  deleteOption,
  updateOptionValues,
  saveSelections,
  addNewOption,
}) => {
  // Function to extract unique options (e.g., sizes or colors)
  const extractUniqueOptions = (variants: VariantItem[], key: string) => {
    const uniqueValues = Array.from(new Set(variants.map((variant) => variant.values[key])));
    return uniqueValues.map((val) => ({ text: val, value: val }));
  };

  return (
    <div>
      <div className="pb-2 flex items-stretch overflow-hidden">
        <figure className={`h-12.5 w-12.5 rounded-10 overflow-hidden flex-shrink-0`}>
          <img src={getItemThumbnail(product)} alt="" className="h-full w-full object-cover" />
        </figure>
        <div className="ml-3.75 flex flex-col gap-y-0.5 flex-1 overflow-hidden justify-center">
          <h5
            className={`text-black overflow-hidden text-1sm max-w-full whitespace-nowrap overflow-ellipsis font-display font-bold -mb-1`}
          >
            {capitalizeFirstLetter(product?.name)}{" "}
          </h5>
          <span className={`text-black overflow-hidden mb-1.5 text-sm max-w-full whitespace-nowrap font-body`}>
            {formatAsCurrency(getActualPrice(product))}
          </span>
        </div>
      </div>
      <div className="flex flex-col gap-y-3.5">
        {internalSelected.map((selectedItem, index) => (
          <>
            {selectedItem.state === "preview" ? (
              <div className="flex flex-col gap-y-3.5">
                <div
                  key={index}
                  className={classNames(
                    "flex w-full flex-col bg-grey-fields-100 rounded-15  border-2 overflow-hidden transition-all duration-200 ease-out",
                    {
                      "border-accent-red-500": selectedItem.error,
                      "border-grey-fields-100": !selectedItem.error,
                    }
                  )}
                >
                  {optionKeys.map((optionKey, id) => (
                    <div
                      key={id}
                      className={`flex px-3 md:px-3.5 py-2 md:py-2.5 items-center bg-white ${
                        id < optionKeys.length - 1 ? "border-b-2 border-grey-fields-100" : "rounded-b-15"
                      } border-grey-fields-100 justify-between cursor-pointer`}
                    >
                      <h5 className="font-medium text-xs md:text-sm text-black-placeholder">
                        {enumToHumanFriendly(capitalizeFirstLetter(optionKey))}:{" "}
                        <b>{enumToHumanFriendly(selectedItem.values[optionKey])}</b>
                      </h5>
                      {id === 0 && (
                        <button
                          onClick={() => updateOptionState(index, StateType.FORM)}
                          className={`transition-all duration-100 flex text-1xs gap-2 ease-out transform text-black-placeholder font-medium`}
                        >
                          {/* prettier-ignore */}
                          <svg width="16" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M9 14.9998H15.75M12.282 2.7163C12.5805 2.41773 12.9855 2.25 13.4077 2.25C13.83 2.25 14.2349 2.41773 14.5335 2.7163C14.8321 3.01487 14.9998 3.41981 14.9998 3.84205C14.9998 4.26429 14.8321 4.66923 14.5335 4.9678L5.52598 13.9761C5.34755 14.1545 5.127 14.285 4.88473 14.3556L2.73073 14.9841C2.6662 15.0029 2.59779 15.004 2.53266 14.9873C2.46754 14.9706 2.4081 14.9368 2.36057 14.8892C2.31303 14.8417 2.27915 14.7822 2.26246 14.7171C2.24578 14.652 2.24691 14.5836 2.26573 14.5191L2.89423 12.3651C2.9649 12.1231 3.09542 11.9028 3.27373 11.7246L12.282 2.7163Z" stroke="#747478" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                          Edit Options
                        </button>
                      )}
                    </div>
                  ))}
                  <div className="rounded-b-xl py-2 px-3 md:py-2.5 md:px-3.5 flex justify-between items-center">
                    <p className="text-xs md:text-sm font-bold text-black">
                      {formatAsCurrency(
                        getActualPrice(getCustomVariantOption(selectedItem.values, product?.variants.options)) *
                          selectedItem.quantity
                      )}
                    </p>

                    <dd className={`no-outline inline-flex items-center rounded-full flex-shrink-0`}>
                      <QuantityUpdate
                        value={selectedItem.quantity}
                        onChange={(qty: number) => updateOptionQuantity(index, qty)}
                        increment={() => updateOptionQuantity(index, selectedItem.quantity + 1)}
                        decrement={() => updateOptionQuantity(index, selectedItem.quantity - 1)}
                        deleteAction={() => deleteOption(index)}
                      />
                    </dd>
                  </div>
                </div>
                {selectedItem.error && <span className="text-xs text-accent-red-500">{selectedItem.error}</span>}
              </div>
            ) : (
              <div
                className={classNames("flex flex-col border rounded-15 p-2.5 transition-all duration-200 ease-out", {
                  "border-accent-red-500": selectedItem.error,
                  "border-grey-border border-opacity-50": !selectedItem.error,
                })}
              >
                <div className="grid grid-cols-2 mt-1.5 gap-3 gap-y-4">
                  {variantOptions.map((option, _index) => (
                    <div
                      key={_index}
                      className={classNames({
                        "col-span-2": variantOptions.length % 2 !== 0 && _index === variantOptions.length - 1,
                      })}
                    >
                      <SelectDropdown
                        label={enumToHumanFriendly(option)}
                        options={extractUniqueOptions(product?.variants.options, option)}
                        caretHasBg
                        value={selectedItem.values[option] ? selectedItem.values[option] : ""}
                        onChange={(e) => updateOptionValues(index, option, e.target.value)}
                      />
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-2.5">
                  {selectedItem.error && (
                    <span className="text-xs text-accent-red-500 font-medium">{selectedItem.error}</span>
                  )}
                  <AppBtn
                    type="button"
                    color="neutral"
                    className="inline-flex ml-auto !h-8"
                    size="sm"
                    onClick={() => saveSelections(index)}
                  >
                    Done
                  </AppBtn>
                </div>
              </div>
            )}
          </>
        ))}
      </div>
      <button
        onClick={addNewOption}
        className="mt-5 mb-3 flex items-center text-primary-500 text-sm font-semibold"
        type="button"
      >
        {/* prettier ignore */}
        <svg viewBox="0 0 14 14" fill="none" className="w-4 mr-1">
          <path
            d="M7 2.91675V11.0834"
            stroke="currentColor"
            stroke-width="1.2"
            stroke-linecap="round"
            stroke-linejoin="round"
          ></path>
          <path
            d="M2.91675 7H11.0834"
            stroke="currentColor"
            stroke-width="1.2"
            stroke-linecap="round"
            stroke-linejoin="round"
          ></path>
        </svg>
        Select New
      </button>
    </div>
  );
};

export default SelectMultipleCustomOptions;
